import { useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import ConfigTabs from "../components/ConfigTabs";
import useSettings from "../hooks/useSettings";
import { Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

// Sensible defaults seeded into the table when admin opens the page first time
const DEFAULTS = [
  { id: "facebook", label: "Facebook", slug: "facebook", color: "#1877f2", gradient: "", url: "", enabled: true, order: 1 },
  { id: "instagram", label: "Instagram", slug: "instagram", color: "#e1306c", gradient: "linear-gradient(135deg,#feda77 0%,#f58529 25%,#dd2a7b 50%,#8134af 75%,#515bd4 100%)", url: "", enabled: true, order: 2 },
  { id: "imo", label: "IMO", slug: "imo", color: "#19a8e1", gradient: "", url: "", enabled: true, order: 3 },
  { id: "tiktok", label: "TikTok", slug: "tiktok", color: "#000000", gradient: "linear-gradient(135deg,#25f4ee 0%,#000000 50%,#fe2c55 100%)", url: "", enabled: true, order: 4 },
  { id: "x", label: "X", slug: "x", color: "#000000", gradient: "", url: "", enabled: true, order: 5 },
  { id: "pinterest", label: "Pinterest", slug: "pinterest", color: "#bd081c", gradient: "", url: "", enabled: true, order: 6 },
  { id: "youtube", label: "YouTube", slug: "youtube", color: "#ff0000", gradient: "", url: "", enabled: true, order: 7 },
  { id: "telegram", label: "Telegram", slug: "telegram", color: "#26a5e4", gradient: "", url: "", enabled: true, order: 8 },
  { id: "whatsapp", label: "WhatsApp", slug: "whatsapp", color: "#25d366", gradient: "", url: "", enabled: true, order: 9 },
];

const slugify = (s) => String(s || "")
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "");

const newRow = () => ({
  id: `social-${Math.random().toString(36).slice(2, 8)}`,
  label: "",
  slug: "",
  color: "#19a55c",
  gradient: "",
  url: "",
  enabled: true,
  order: 0,
});

export default function ConfigSocial() {
  const { form, set, save, loading } = useSettings();
  const [rows, setRows] = useState(null);

  // Sync the form's social_links into local state
  useEffect(() => {
    if (!form) return;
    const fromForm = Array.isArray(form.social_links) ? form.social_links : null;
    if (fromForm && fromForm.length > 0) {
      setRows(fromForm.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
    } else {
      setRows(DEFAULTS.slice());
    }
  }, [form]);

  const sorted = useMemo(
    () => (rows || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [rows],
  );

  if (!form || !rows) {
    return (
      <div className="bg-white border border-gray-200 rounded-sm p-10 text-center text-sm text-gray-500">
        Loading...
      </div>
    );
  }

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));
  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { ...newRow(), order: (prev[prev.length - 1]?.order ?? prev.length) + 1 },
    ]);
  const moveRow = (idx, dir) => {
    setRows((prev) => {
      const next = prev.slice();
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      // re-number order
      return next.map((r, i) => ({ ...r, order: i + 1 }));
    });
  };

  const handleSave = async () => {
    // Re-number order based on current visual order, autofill slug if missing
    const cleaned = sorted.map((r, i) => ({
      ...r,
      slug: r.slug ? slugify(r.slug) : slugify(r.label),
      order: i + 1,
    }));
    set("social_links", cleaned);
    // Pass directly to save so we don't rely on stale form state
    await save({ social_links: cleaned });
  };

  return (
    <div data-testid="config-social-page">
      <PageHeader
        title="Configuration"
        subtitle="Social · add, remove, reorder and toggle social channels shown in the public site footer"
      />
      <ConfigTabs />

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 max-w-5xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Social Channels</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Icon comes from the slug (e.g. <code className="bg-gray-100 px-1 rounded">facebook</code>, <code className="bg-gray-100 px-1 rounded">whatsapp</code>) — see{" "}
              <a className="text-emerald-600 hover:underline" href="https://simpleicons.org/" target="_blank" rel="noreferrer">simpleicons.org</a>.
            </p>
          </div>
          <button
            type="button"
            onClick={addRow}
            className="h-9 px-3 inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm text-xs font-semibold hover:bg-emerald-100"
            data-testid="cfg-social-add"
          >
            <Plus size={13} /> Add channel
          </button>
        </div>

        <div className="space-y-2">
          {sorted.length === 0 && (
            <p className="text-xs text-gray-400 italic px-1">No social channels yet. Click "Add channel" to start.</p>
          )}
          {sorted.map((r, idx) => {
            const realIdx = rows.indexOf(r);
            const previewSlug = r.slug || slugify(r.label) || "link";
            return (
              <div
                key={r.id || idx}
                className={`border rounded-sm p-3 sm:p-3.5 grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center ${r.enabled === false ? "bg-gray-50 border-gray-200 opacity-75" : "bg-white border-gray-200"}`}
                data-testid={`cfg-social-row-${idx}`}
              >
                {/* Live preview swatch */}
                <div className="sm:col-span-1 flex justify-center">
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ background: r.gradient || r.color || "#19a55c" }}
                    title={r.label}
                  >
                    <img
                      src={`https://cdn.simpleicons.org/${previewSlug}/ffffff`}
                      alt=""
                      className="h-[55%] w-[55%] object-contain"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                </div>

                {/* Label */}
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Label</label>
                  <input
                    value={r.label || ""}
                    onChange={(e) => updateRow(realIdx, { label: e.target.value })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-sm text-xs"
                    placeholder="Facebook"
                    data-testid={`cfg-social-label-${idx}`}
                  />
                </div>

                {/* Slug (icon name) */}
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Icon slug</label>
                  <input
                    value={r.slug || ""}
                    onChange={(e) => updateRow(realIdx, { slug: e.target.value })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-sm text-xs font-mono"
                    placeholder="facebook"
                    data-testid={`cfg-social-slug-${idx}`}
                  />
                </div>

                {/* Color */}
                <div className="sm:col-span-1">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">Color</label>
                  <input
                    type="color"
                    value={r.color || "#19a55c"}
                    onChange={(e) => updateRow(realIdx, { color: e.target.value })}
                    className="w-full h-8 border border-gray-200 rounded-sm cursor-pointer"
                    data-testid={`cfg-social-color-${idx}`}
                  />
                </div>

                {/* URL */}
                <div className="sm:col-span-4">
                  <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-0.5">URL</label>
                  <input
                    value={r.url || ""}
                    onChange={(e) => updateRow(realIdx, { url: e.target.value })}
                    className="w-full h-8 px-2 border border-gray-200 rounded-sm text-xs font-mono"
                    placeholder="https://facebook.com/maxlive88"
                    data-testid={`cfg-social-url-${idx}`}
                  />
                </div>

                {/* Actions */}
                <div className="sm:col-span-2 flex items-end justify-end gap-1">
                  <button
                    type="button"
                    onClick={() => updateRow(realIdx, { enabled: !(r.enabled !== false) })}
                    className={`h-8 w-8 flex items-center justify-center border rounded-sm text-xs ${r.enabled === false ? "bg-gray-100 text-gray-500 border-gray-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}
                    title={r.enabled === false ? "Enable" : "Disable"}
                    data-testid={`cfg-social-toggle-${idx}`}
                  >
                    {r.enabled === false ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button
                    type="button"
                    onClick={() => moveRow(realIdx, -1)}
                    disabled={idx === 0}
                    className="h-8 w-8 flex items-center justify-center bg-white text-gray-700 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Move up"
                    data-testid={`cfg-social-up-${idx}`}
                  >
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveRow(realIdx, 1)}
                    disabled={idx === sorted.length - 1}
                    className="h-8 w-8 flex items-center justify-center bg-white text-gray-700 border border-gray-200 rounded-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Move down"
                    data-testid={`cfg-social-down-${idx}`}
                  >
                    <ArrowDown size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeRow(realIdx)}
                    className="h-8 w-8 flex items-center justify-center bg-red-50 text-red-700 border border-red-200 rounded-sm hover:bg-red-100"
                    title="Remove"
                    data-testid={`cfg-social-remove-${idx}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end pt-4 mt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="cfg-social-save"
          >
            <Save size={14} /> {loading ? "Saving..." : "Save Social Links"}
          </button>
        </div>
      </div>
    </div>
  );
}
